import { useTranslation } from "react-i18next";

import { useSelectedDealer } from "components/DealersAndLocationsDropdown/hooks";
import { MANUFACTURER_REQUEST_STATUSES, PIN_STATUSES, PIN_TYPES, WARRANTY_TYPES } from "models";
import { MANUFACTURER_QUESTION_RESULT_STATUS_FILTER } from "modules/ManufacturerDashboard/queryKeys";
import { REG_VIN_FILTER } from "modules/WarrantyDashboard/hooks";

export const useManufacturerDropdownOptions = () => {
  const { t } = useTranslation();

  const { selectedDealer } = useSelectedDealer();

  const regHeaderOptions = [
    { key: REG_VIN_FILTER.Reg, text: t("v8_reg", "Reg #"), value: REG_VIN_FILTER.Reg },
    { key: REG_VIN_FILTER.Vin, text: t("v8_vin", "Vin #"), value: REG_VIN_FILTER.Vin }
  ];

  if (!selectedDealer) {
    return {
      pinTypeDropdownOptions: [],
      questionStatusDropdownOptions: [],
      manufacturerRequestStatusDropdownOptions: [],
      pinStatusDropdownOptions: [],
      warrantyDropdownOptions: [],
      mechanicFixedDropdownOptions: [],
      regHeaderOptions
    };
  }

  const pinTypeDropdownOptions = [
    { key: 0, text: t("v8_all", "All"), value: "all" },
    { key: PIN_TYPES.Remarks, text: t("v8_remarks", "Remarks"), value: PIN_TYPES.Remarks, icon: "thumb tack", iconColor: "#2084CE" },
    { key: PIN_TYPES.Warranty, text: t("v8_warranty", "Warranty"), value: PIN_TYPES.Warranty, icon: "shield halved", iconColor: "#C83627" },
    { key: PIN_TYPES.Recall, text: t("v8_recall", "Recall"), value: PIN_TYPES.Recall, icon: "registered", iconColor: "#2084CE" }
  ];

  const questionStatusDropdownOptions = [
    { key: 0, text: t("v8_all", "All"), value: "all" },
    {
      key: MANUFACTURER_QUESTION_RESULT_STATUS_FILTER.Ok,
      text: t("v8_ok", "Ok"),
      value: MANUFACTURER_QUESTION_RESULT_STATUS_FILTER.Ok,
      icon: "file",
      iconColor: "#21BA45"
    },
    {
      key: MANUFACTURER_QUESTION_RESULT_STATUS_FILTER.Advise,
      text: t("v8_advise", "Advise"),
      value: MANUFACTURER_QUESTION_RESULT_STATUS_FILTER.Advise,
      icon: "exclamation triangle",
      iconColor: "orange"
    },
    {
      key: MANUFACTURER_QUESTION_RESULT_STATUS_FILTER.Critical,
      text: t("v8_critical", "Critical"),
      value: MANUFACTURER_QUESTION_RESULT_STATUS_FILTER.Critical,
      icon: "exclamation circle",
      iconColor: "red"
    }
  ];

  const manufacturerRequestStatusDropdownOptions = [
    { key: 0, text: t("v8_all", "All"), value: "all" },
    {
      key: MANUFACTURER_REQUEST_STATUSES.Pending,
      text: t("v8_pending", "Pending"),
      value: MANUFACTURER_REQUEST_STATUSES.Pending,
      icon: "question",
      iconColor: "#054299"
    },
    {
      key: MANUFACTURER_REQUEST_STATUSES.Approved,
      text: t("v8_approved", "Approved"),
      value: MANUFACTURER_REQUEST_STATUSES.Approved,
      icon: "check",
      iconCcolor: "#21BA45"
    },
    { key: MANUFACTURER_REQUEST_STATUSES.Declined, text: t("v8_declined", "Declined"), value: MANUFACTURER_REQUEST_STATUSES.Declined, icon: "x", iconColor: "#FF0001" }
  ];

  const pinStatusDropdownOptions = [
    { key: PIN_STATUSES.NotSet, text: t("v8_not_set", "Not Set"), value: PIN_STATUSES.NotSet },
    { key: PIN_STATUSES.Claimed, text: t("v8_claimed", "Claimed"), value: PIN_STATUSES.Claimed },
    { key: PIN_STATUSES.Support, text: t("v8_support", "Support"), value: PIN_STATUSES.Support },
    { key: PIN_STATUSES.ApprovalPending, text: t("v8_approval_pending", "Approval Pending"), value: PIN_STATUSES.ApprovalPending },
    { key: PIN_STATUSES.MoreInfo, text: t("v8_more_info", "More Info"), value: PIN_STATUSES.MoreInfo },
    { key: PIN_STATUSES.Approved, text: t("v8_approved", "Approved"), value: PIN_STATUSES.Approved },
    { key: PIN_STATUSES.Declined, text: t("v8_declined", "Declined"), value: PIN_STATUSES.Declined },
    { key: PIN_STATUSES.ToClaim, text: t("v8_to_claim", "To Claim"), value: PIN_STATUSES.ToClaim },
    { key: PIN_STATUSES.Billed, text: t("v8_billed", "Billed"), value: PIN_STATUSES.Billed },
    { key: PIN_STATUSES.Request, text: t("v8_request", "Request"), value: PIN_STATUSES.Request },
    { key: PIN_STATUSES.TechnicalInfo, text: t("v8_technical_info", "Technical Info"), value: PIN_STATUSES.TechnicalInfo }
  ];

  const mechanicFixedDropdownOptions = [
    { key: 1, text: t("v8_yes", "Yes"), value: true },
    { key: 2, text: t("v8_no", "No"), value: false }
  ];

  const warrantyDropdownOptions = [
    { key: `pin-warranty-all`, value: "all", text: t("v8_all", "All") },
    { key: `pin-warranty-${WARRANTY_TYPES.NotSet}`, value: WARRANTY_TYPES.NotSet, text: t("v8_not_set", "Not Set") },
    { key: `pin-warranty-${WARRANTY_TYPES.Factory}`, value: WARRANTY_TYPES.Factory, text: t("v8_factory", "Factory") },
    { key: `pin-warranty-${WARRANTY_TYPES.Leniency}`, value: WARRANTY_TYPES.Leniency, text: t("v8_leniency", "Leniency") },
    { key: `pin-warranty-${WARRANTY_TYPES.BodyPaint}`, value: WARRANTY_TYPES.BodyPaint, text: t("v8_body_paint", "Body Paint") },
    { key: `pin-warranty-${WARRANTY_TYPES.OnRepair}`, value: WARRANTY_TYPES.OnRepair, text: t("v8_on_repair", "On Repair") },
    { key: `pin-warranty-${WARRANTY_TYPES.Occasion}`, value: WARRANTY_TYPES.Occasion, text: t("v8_occasion", "Occasion") },
    { key: `pin-warranty-${WARRANTY_TYPES.Service}`, value: WARRANTY_TYPES.Service, text: t("v8_service", "Service") }
  ];

  return {
    pinTypeDropdownOptions: pinTypeDropdownOptions,
    questionStatusDropdownOptions: questionStatusDropdownOptions,
    manufacturerRequestStatusDropdownOptions: manufacturerRequestStatusDropdownOptions,
    pinStatusDropdownOptions: pinStatusDropdownOptions,
    warrantyDropdownOptions: warrantyDropdownOptions,
    mechanicFixedDropdownOptions,
    regHeaderOptions
  };
};
